import { saveAs } from 'file-saver';
import { useEffect, useState } from 'react';

import {
    deletePolicyFile,
    getPolicyFile,
    getPolicyFileList,
    postPolicyFile,
} from 'api/clinicalPolicyAPI';
import { handleError } from 'services/Error';

export const usePolicyFileList = ({
    payerId,
    draftId,
    isOpen,
}: {
    payerId?: string;
    draftId?: string;
    isOpen: boolean;
}) => {
    const [fileList, setFileList] = useState<{ name: string; size: number }[]>();
    useEffect(() => {
        void (async () => {
            try {
                if (payerId && draftId && isOpen) {
                    setFileList(await getPolicyFileList({ payerId, draftId }));
                }
            } catch (err) {
                handleError('An error occurred retrieving uploaded files.', err as Error);
            }
        })();
    }, [payerId, draftId, isOpen]);
    return fileList;
};

export const downloadPolicyFile = async ({
    payerId,
    draftId,
    filename,
}: {
    payerId: string;
    draftId: string;
    filename: string;
}) => {
    try {
        saveAs(await getPolicyFile({ payerId, draftId, filename }), filename);
    } catch (err) {
        handleError(`An error occurred downloading ${filename}.`, err as Error);
    }
};

export const uploadPolicyFiles = async ({
    payerId,
    draftId,
    files,
}: {
    payerId: string;
    draftId: string;
    files: File[];
}) => {
    try {
        await Promise.all(files.map(async (file) => postPolicyFile({ payerId, draftId, file })));
    } catch (err) {
        handleError('An error occurred uploading files.', err as Error);
        throw err; // Propagate error to FileUpload component
    }
};

export const removePolicyFile = async ({
    payerId,
    draftId,
    filename,
}: {
    payerId: string;
    draftId: string;
    filename: string;
}) => {
    try {
        await deletePolicyFile({ payerId, draftId, filename });
    } catch (err) {
        handleError(`An error occurred deleting ${filename}.`, err as Error);
        throw err; // Propagate error to FileUpload component
    }
};
